/**
 * Iris's model catalog and provider routing.
 *
 * Extracted from HelperChat.tsx because two independent production defects came
 * from the same mechanical cause: the list of models Iris OFFERS, the map from a
 * model id to the BACKEND ROUTE that can run it, and the set of ids that are
 * still ALIVE at the provider were three separate hardcoded things that nothing
 * compared. They drifted, in both directions at once:
 *
 *   1. The default was `minimax-m2.5`, retired by Ollama on 2026-07-31. A new
 *      user's very first message reserved credits, 404'd and refunded.
 *   2. `kimi-k2.6` was offered but had no routing branch, so it fell through to
 *      /api/openai/chat, which reserves under provider 'openai'. No
 *      model_registry row exists for a kimi id under that provider, so every
 *      Kimi message 500'd before the request was sent.
 *
 * Keeping all three here as data lets src/components/__tests__/irisModels.test.ts
 * assert they agree, which is what makes a repeat of either defect a test
 * failure rather than a support ticket.
 */

export interface IrisModelOption {
  value: string;
  label: string;
}

export interface IrisModelGroup {
  label: string;
  options: IrisModelOption[];
}

/**
 * Iris's default model — what a brand-new user's very first message runs on.
 * It must be live at the provider AND priced in every layer (MODEL_REGISTRY,
 * creditCostUtils, and server-side model_registry / CreditService fallbacks).
 *
 * minimax-m2.7 is the only MiniMax tag Ollama Cloud still serves, is the MiniMax
 * node's own default, and costs the same 3 cr as the retired minimax-m2.5 it
 * replaces — so no existing user sees a price change.
 */
export const IRIS_DEFAULT_MODEL = 'minimax-m2.7';

/**
 * Every model Iris offers, grouped for the settings <select>.
 *
 * NOT derived from MODEL_REGISTRY on purpose: Iris is a chat assistant, not a
 * canvas node, and only a curated subset of text models makes sense here. But
 * every id below is asserted priceable and routable by the test.
 */
export const IRIS_MODEL_GROUPS: IrisModelGroup[] = [
  {
    label: 'OpenAI',
    options: [
      { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano (fast)' },
      { value: 'gpt-5.6-luna', label: 'GPT-5.6 Luna (fast, cheap)' },
      { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra (balanced)' },
      { value: 'gpt-5.6-sol', label: 'GPT-5.6 Sol (flagship)' },
    ],
  },
  {
    label: 'Anthropic',
    options: [
      { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 (fast)' },
      { value: 'claude-sonnet-5', label: 'Claude Sonnet 5 (high quality)' },
      { value: 'claude-opus-5', label: 'Claude Opus 5 (flagship)' },
    ],
  },
  {
    label: 'MiniMax (Ollama Cloud)',
    // minimax-m2.5 removed: retired by Ollama 2026-07-31.
    options: [
      { value: 'minimax-m2.7', label: 'MiniMax M2.7 (default, agents)' },
    ],
  },
  {
    label: 'Qwen (Ollama Cloud)',
    // qwen-3-vl-235b removed earlier: its Ollama Cloud tag does not exist.
    options: [
      { value: 'qwen-3.5', label: 'Qwen 3.5 (multimodal, 256K)' },
      { value: 'qwen-3.5-397b', label: 'Qwen 3.5 397B (multimodal)' },
    ],
  },
  {
    label: 'Kimi (Ollama Cloud)',
    // kimi-k2.5 removed: retired by Ollama 2026-07-31.
    options: [
      { value: 'kimi-k2.6', label: 'Kimi K2.6 (256K, vision)' },
    ],
  },
];

/** Flat list of every offered id. */
export const IRIS_MODEL_IDS: string[] = IRIS_MODEL_GROUPS.flatMap(g => g.options.map(o => o.value));

/**
 * Model-id prefix → backend route that can actually run it.
 *
 * Order matters only in that the first matching prefix wins; the prefixes are
 * disjoint today. Every prefix present in IRIS_MODEL_GROUPS must appear here —
 * the test enforces it — because the historical failure mode was a silent
 * fall-through to the OpenAI route, which reserves credits under provider
 * 'openai' and 500s on a registry miss instead of reporting the mismatch.
 */
const ENDPOINT_BY_PREFIX: ReadonlyArray<readonly [string, string]> = [
  ['gpt-', '/api/openai/chat'],
  ['claude-', '/api/claude/chat'],
  ['minimax-', '/api/minimax/chat'],
  ['qwen-', '/api/qwen/chat'],
  ['kimi-', '/api/kimi/chat'],
];

/**
 * The route for a model id, or null when no prefix matches.
 *
 * Callers fall back to the OpenAI route to preserve behaviour for a hand-edited
 * localStorage value, but a null here for an id Iris actually offers is a bug
 * and is asserted against in the test.
 */
export function irisEndpointForModel(model: string): string | null {
  const hit = ENDPOINT_BY_PREFIX.find(([prefix]) => model.startsWith(prefix));
  return hit ? hit[1] : null;
}

/**
 * Model ids retired at the provider after users may already have saved them to
 * localStorage. Without this remap those users stay pinned to a dead id and see
 * an empty <select>, because the option itself is gone from the dropdown.
 *
 * Each maps to the same successor the server aliases it to
 * (server/routes/{minimax,kimi}.ts, server/services/ProviderService.ts) and to
 * the same 3 cr price, so remapping changes neither behaviour nor the bill.
 */
export const IRIS_RETIRED_MODELS: Readonly<Record<string, string>> = {
  'minimax-m2.5': IRIS_DEFAULT_MODEL, // retired 2026-07-31
  'minimax-m2.1': IRIS_DEFAULT_MODEL, // retired 2026-07-15
  'minimax-m2': IRIS_DEFAULT_MODEL,   // retired 2026-06-16
  'kimi-k2.5': 'kimi-k2.6',           // retired 2026-07-31
};

/** Normalise a stored model id: missing → default, retired → successor. */
export function resolveIrisModel(stored: unknown): string {
  if (typeof stored !== 'string' || stored === '') return IRIS_DEFAULT_MODEL;
  return IRIS_RETIRED_MODELS[stored] ?? stored;
}
